/**
 * Per-user reset of the B2C onboarding stores.
 *
 * BUG-3: the two onboarding Zustand persist stores are GLOBAL localStorage keys
 * (`individual-onboarding-store` → `elreign:individual-onboarding:v3`,
 * `setup-store` → `elreign:setup:v1`). They are NOT scoped or reset per user, so
 * a new/different authenticated user signing up on the same browser inherits the
 * previous user's outer gates (`provisioned` / `subscriptionDone`) and stale
 * doc-dump state — skipping provisioning AND the paid subscription step
 * (`individual-screen.tsx` warns the subscription gate "must never be skipped on
 * a false signal"; a stale global gate is exactly that).
 *
 * Approach: a single chokepoint, `resetOnboardingStoresOnUserChange(userId)`,
 * that the auth listener (`AuthProvider`) calls on hydrate AND on every
 * auth-state change. It tracks the last-seen authenticated user id (mirrored in
 * localStorage so a reloaded tab carrying a different persisted session still
 * resets) and clears BOTH stores whenever the id transitions to a DIFFERENT
 * non-null user. A sign-out (null id) is recorded but does NOT wipe — the same
 * user may sign back in to resume mid-onboarding; a wipe only happens when the
 * NEXT signed-in id differs from the one that owned the persisted state.
 *
 * Why a reset (not a per-user namespaced key): the stores' own `clear()` already
 * defines the canonical empty state and is what every exit path uses; reusing it
 * keeps one definition of "empty". Namespacing the persist key per user would
 * also leave the previous user's blob in localStorage indefinitely (a POPIA-
 * unfriendly residue on a shared browser) — clearing removes it.
 */

import { useIndividualOnboardingStore } from './individual-onboarding-store';
import { useSetupStore } from './setup-store';
import { useFirmOnboardingStore } from './firm-onboarding-store';
import { clearActAsContext } from './act-as-context';
// The LEAF, never `acting/context` — this module is already inside that file's
// import cycle (acting/context → use-session → auth-context → onboarding-reset),
// so importing the React surface here would close it.
import { forgetLastKnownActAsSubject } from './acting/last-act-as';
import { queryClient } from './query-client';

/** localStorage key holding the last authenticated user id we reconciled
 *  onboarding state against. Survives reloads so a tab reopened with a
 *  different persisted Supabase session still triggers a wipe. */
export const LAST_SEEN_USER_KEY = 'elreign:onboarding-owner:v1';

/** In-memory mirror of the last-seen id, lazily seeded from localStorage on
 *  first use. `undefined` = not yet read; `null` = read, signed out / unknown. */
let lastSeenUserId: string | null | undefined;

/** True once an AUTHENTICATED user id has been seen during THIS page load.
 *  Deliberately in-memory only (never localStorage): it distinguishes a real
 *  sign-out (a session that existed this load ending → the HR-P0 cache purge
 *  must run) from the initial hydrate of an anonymous page load (nothing to
 *  purge — see the null branch below). A reload resets it, which is correct:
 *  the query cache it guards is also per-load memory. */
let sawAuthedUserThisLoad = false;

function safeGetItem(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    /* private-mode / quota — non-fatal; in-memory mirror still guards the session */
  }
}

function safeRemoveItem(key: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
}

function readLastSeen(): string | null {
  if (lastSeenUserId === undefined) {
    lastSeenUserId = safeGetItem(LAST_SEEN_USER_KEY);
  }
  return lastSeenUserId;
}

function writeLastSeen(userId: string | null): void {
  lastSeenUserId = userId;
  if (userId === null) {
    safeRemoveItem(LAST_SEEN_USER_KEY);
  } else {
    safeSetItem(LAST_SEEN_USER_KEY, userId);
  }
}

/** Clear ALL onboarding wrapper stores AND drop their persisted localStorage blobs. */
function clearOnboardingStores(): void {
  // `clear()` resets state to the canonical empty shape; the persist middleware
  // rewrites the (now-empty) blob synchronously, so the stale provisioned:true /
  // subscriptionDone:true gates cannot survive in localStorage either.
  useIndividualOnboardingStore.getState().clear();
  useSetupStore.getState().clear();
  // S0-7 (audit F1): the FIRM (B2B) wrapper store is a global persisted key with
  // the SAME cross-user bleed as the B2C stores — a different firm admin on a
  // shared browser would otherwise inherit the prior firm's gates + created-client
  // names. Reset it on the same chokepoint.
  useFirmOnboardingStore.getState().clear();
}

/**
 * Reconcile the persisted onboarding state against the currently authenticated
 * user. Call on every auth-state change (and once on hydrate).
 *
 *   - First non-null id seen this load → adopt it as the owner (no wipe; this is
 *     either the user who created the persisted state resuming, or a fresh load
 *     where there is nothing to protect).
 *   - Same id again → no-op (mid-onboarding resume / token refresh must NOT lose
 *     progress).
 *   - A DIFFERENT non-null id → WIPE both stores, then adopt the new owner.
 *   - null id (signed out) → no-op; the recorded owner is KEPT so the same user
 *     resumes on return while a DIFFERENT next signed-in user triggers the wipe.
 */
export function resetOnboardingStoresOnUserChange(userId: string | null): void {
  const previous = readLastSeen();

  if (userId === null) {
    // Signed out: the ONBOARDING decision is a no-op — we deliberately KEEP the
    // recorded owner so the same user signing back in resumes (no wipe) and a
    // DIFFERENT user signing in next triggers the wipe. But the act-as /
    // impersonation context IS dropped (S-1.3): you are not administering anyone
    // once signed out, and a re-login must never resume a stale "administering X".
    clearActAsContext();
    // ⛔ AND FORGET WHO IT WAS. `clearActAsContext()` empties the STORE;
    // `lastKnownActAsSubject()` deliberately survives that, because `api()`
    // clears the store as it throws ACT_AS_EXPIRED and the renewal path needs to
    // know which client to re-mint for. Its own comment says the real exits must
    // therefore say so out loud — and until now nothing did:
    // `forgetLastKnownActAsSubject()` had no caller outside its test file.
    //
    // On a shared/office tab (the case the HR-P0 notes below exist to defend)
    // that stranded fact leaks ACROSS USERS: once any operator had administered
    // client X, every later identity render in this tab saw `stoodInFor = X`, so
    // a different user's own onboarding said "Reconnect to X" and
    // "This is X's identity, not yours" — a previous firm's client name printed
    // to someone with no relationship to them.
    forgetLastKnownActAsSubject();
    // HR-P0: also drop the TanStack query cache — but ONLY when a session that
    // existed during THIS page load has ended (a real sign-out). The QueryClient
    // is a module singleton (query-client.ts) that survives sign-out on a
    // shared/office browser (no page reload), so without this the NEXT user to
    // sign in on this tab is served the prior user's cached trust/estate/tenant
    // data straight from the stale cache. Onboarding progress lives in the
    // Zustand stores above, NOT the query cache, so clearing here never loses a
    // resuming same-user's onboarding position.
    //
    // SIGN-UI-1 (2026-07-21): this clear must NOT run on the initial hydrate of
    // an ANONYMOUS page load. The auth listener hydrates ~ms after first paint;
    // on public routes (/sign/external/$token, marketing, the white-label
    // branding fetch) an unconditional clear() removed in-flight public queries
    // from the cache while their useQuery observers stayed mounted — orphaning
    // them permanently (removal cancels the fetch; the observer never receives
    // another state update). The emailed signing screen wedged on its skeleton
    // forever, on prod, for every expired/invalid link. A fresh load's cache is
    // empty of any prior user's data by construction (it is per-load memory),
    // so skipping the purge here gives up nothing HR-P0 needs.
    if (sawAuthedUserThisLoad) {
      queryClient.clear();
    }
    return;
  }

  // An authenticated user exists this load — a later null IS a real sign-out.
  sawAuthedUserThisLoad = true;

  if (previous !== null && previous !== userId) {
    // A different user now owns this browser — the persisted onboarding gates
    // belong to someone else. Wipe before adopting the new owner. Also drop any
    // act-as / impersonation context so the new user never inherits the prior
    // user's "administering X" session (S-1.3).
    clearOnboardingStores();
    clearActAsContext();
    // Same reasoning as the sign-out branch: the STORE is cleared, and the
    // sticky "who was I standing in for" fact has to go with it. This is the
    // direct session-swap path (user B signs in without user A signing out),
    // where the null branch above never ran.
    forgetLastKnownActAsSubject();
    // HR-P0: purge the query cache so user B never renders user A's cached
    // fiduciary data. Covers the direct session-swap path (a different user
    // signs in WITHOUT an explicit sign-out first, e.g. a fresh signup on the
    // same tab), where the null-branch clear above never ran.
    queryClient.clear();
  }

  writeLastSeen(userId);
}

/** Test hook — reset the module-local last-seen memory. By default also clears
 *  the persisted key; pass `{ keepStorage: true }` to simulate a fresh page
 *  load that recovers the last-seen id from localStorage. */
export function __resetLastSeenUserForTests(opts?: { keepStorage?: boolean }): void {
  lastSeenUserId = undefined;
  sawAuthedUserThisLoad = false;
  if (!opts?.keepStorage) safeRemoveItem(LAST_SEEN_USER_KEY);
}
